import React from "react";
import { useTranslation } from "react-i18next";

import { InfoPlate } from "fe-shared/src/components";
import AssigneeTransfersSelect from "components/AssigneeTransfersSelect";

import {
  InvalidationStatus,
  PaperFormIndicator,
  TransferStatusPlate,
} from "fe-shared-transactions/src/components";
import { formatTransactionNumber } from "fe-shared-transactions/src/helpers/format";
import {
  Transfer,
  TransferType,
  TransferStatus as StatusCode,
} from "fe-shared-transactions/src/types";
import { priceToFormat } from "fe-shared-transactions/src/helpers/invoice";

import styles from "./TransferStatus.module.scss";

const ASSIGNABLE_TRANSFER_TYPES: TransferType[] = [
  TransferType.NonEligibleWriteOff,
  TransferType.NonEligibleBufferWriteOff,
];

interface Props {
  canAssign?: boolean;
  transfer: Transfer;
}

const TransferStatus = (props: Props) => {
  const {
    assignee,
    statusCode,
    isPaperForm,
    isAdjustable,
    transferNumber,
    transferType,
    paymentAmount,
  } = props.transfer;

  const { t } = useTranslation();
  const isAssigned = !!assignee?.userId;
  const isAssignable = ASSIGNABLE_TRANSFER_TYPES.includes(transferType);
  const shouldShowPaymentAmount =
    statusCode !== StatusCode.Invoiced &&
    paymentAmount !== null &&
    paymentAmount !== undefined;

  return (
    <div className={styles.header}>
      <div className={styles.status}>
        <InfoPlate variant="black" className={styles.statusInfoPlate}>
          {formatTransactionNumber(transferNumber)}
        </InfoPlate>
        <TransferStatusPlate
          isAdjustable={isAdjustable}
          statusCode={statusCode}
          className={styles.statusInfoPlate}
        />
        {shouldShowPaymentAmount && (
          <InfoPlate variant="info" className={styles.statusInfoPlate}>
            {t("transactions.details.paymentAmountValue", {
              paymentAmount: priceToFormat(paymentAmount || 0),
            })}
          </InfoPlate>
        )}
        <PaperFormIndicator isPaperForm={isPaperForm} />
        {isAssignable && (
          <div className={styles.statusAssign}>
            {isAssigned && (
              <span className={styles.statusAssignLabel}>
                {t("transactions.details.assignedTo")}
              </span>
            )}
            <AssigneeTransfersSelect
              canAssign={props.canAssign}
              transfer={props.transfer}
              assigneeId={assignee?.userId}
              assigneeName={assignee?.displayName ?? undefined}
            />
          </div>
        )}
      </div>
      <InvalidationStatus transactionOrTransfer={props.transfer} />
    </div>
  );
};

export default TransferStatus;
